var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Icon, Tooltip, TooltipPortal } from "@animech/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Styled from "./centralized-pump-overlay-content.style";
import { CentralizedPumpComponentInfo } from "./centralized-pump-component-info";
import { EditButtons, } from "ui/components/elements/edit-buttons/edit-buttons";
import { PaddingLessTooltipClassName } from "ui/app/global.style";
import { ComponentSection } from "ui/components/panels/canvas/node-components/component-select/component-section";
import { FamilyAccordionSelect } from "ui/components/panels/canvas/node-components/component-select/family-accordion-select";
import { PumpChoiceSelect } from "ui/components/panels/canvas/node-components/component-select/pump-choice-select";
import { useCentralPumpOptions } from "hooks/use-central-pump-options";
import { useResultAlphabeticalId } from "hooks/use-result-alphabetical-id";
import { AlphabeticalNodeType } from "hooks/graph-panel/graph";
import { noPumpImage } from "3d/constants/common";
export var CentralizedPumpOverlayContent = function (_a) {
    var editActions = _a.actions, node = _a.node;
    var _b = __read(useState(), 2), height = _b[0], setHeight = _b[1];
    var _c = __read(useState(false), 2), preRenderCompleted = _c[0], setPreRenderCompleted = _c[1];
    var _d = __read(useState(false), 2), sectionOpen = _d[0], setSectionOpen = _d[1];
    var ref = useRef(null);
    var tooltipResizeObserverRef = useRef();
    var updateRef = useRef();
    var tooltipContentRef = useCallback(function (node) {
        var _a;
        (_a = tooltipResizeObserverRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        if (node) {
            tooltipResizeObserverRef.current = new ResizeObserver(function () {
                var _a;
                (_a = updateRef.current) === null || _a === void 0 ? void 0 : _a.call(updateRef);
            });
            tooltipResizeObserverRef.current.observe(node);
        }
    }, []);
    var shouldBeOpenInitially = !sectionOpen;
    var _e = __read(useState(shouldBeOpenInitially), 2), open = _e[0], setOpen = _e[1];
    var t = useTranslation().t;
    var nodeIds = useMemo(function () { return [node.id]; }, [node.id]);
    var _f = useCentralPumpOptions(nodeIds), pumpFamilyOptions = _f.pumpFamilyOptions, pumpFamilyImages = _f.pumpFamilyImages, selectedPumpFamilies = _f.selectedPumpFamilies, onPickPumpFamily = _f.onPickPumpFamily, pumpItemOptions = _f.pumpItemOptions, pumpChoice = _f.pumpChoice, feedPressureUnit = _f.feedPressureUnit, selectedValues = _f.selectedValues, selectedLabel = _f.selectedLabel, selectedImage = _f.selectedImage, onSelectPump = _f.onSelectPump;
    var alphabeticalId = useResultAlphabeticalId(node.id, AlphabeticalNodeType.PUMP);
    useEffect(function () {
        setTimeout(function () {
            setPreRenderCompleted(true);
        });
    }, []);
    useEffect(function () {
        var observer = new ResizeObserver(function (_a) {
            var _b = __read(_a, 1), entry = _b[0];
            setHeight(entry.contentRect.height);
        });
        if (ref.current)
            observer.observe(ref.current);
        return function () { return observer.disconnect(); };
    }, []);
    useEffect(function () {
        return function () { var _a; return (_a = tooltipResizeObserverRef.current) === null || _a === void 0 ? void 0 : _a.disconnect(); };
    }, []);
    return (_jsx(TooltipPortal, { placement: "right", open: sectionOpen, updateRef: updateRef, anchorElement: _jsx("div", { style: {
                pointerEvents: "auto",
                visibility: preRenderCompleted ? "visible" : "hidden",
            }, onWheel: function (e) { return e.stopPropagation(); }, onMouseDown: function (e) { return e.stopPropagation(); }, children: _jsxs(Styled.Root, { ref: ref, children: [_jsx(Styled.EditButtonsContainer, { children: _jsx(EditButtons, { actions: editActions }) }), _jsxs(Styled.ComponentSelectionRoot, { children: [_jsx(Styled.ChevronContainer, { children: _jsx(Styled.Chevron, { onClick: function () { return setOpen(!open); }, children: _jsx(Icon, { name: open ? "chevron-up" : "chevron-down", color: "primary" }) }) }), _jsx(Styled.SectionList, { state: open || !preRenderCompleted ? "open" : "closed", children: _jsx(ComponentSection, { header: t("sidebar.edit_pump.pump_dropdown_label"), alphabeticalId: alphabeticalId, stacked: true, selected: sectionOpen, onClick: function () { return setSectionOpen(!sectionOpen); }, 
                                    /* A pump node without a pump is the placeholder the user
                                     * still has to pick a pump for; say so instead of
                                     * showing an empty value. */
                                    value: selectedLabel !== null && selectedLabel !== void 0 ? selectedLabel : t("sidebar.edit_pump.no_pump_selected") }) })] }), _jsx(CentralizedPumpComponentInfo, { nodeId: node.id })] }) }), children: _jsx(Tooltip, { className: PaddingLessTooltipClassName, color: "neutral", children: _jsx(Styled.TooltipContent, { ref: tooltipContentRef, style: { minHeight: height }, 
                // The popup is portalled out of the anchor that stops the wheel, so it
                // stops it too: a zoom would hide the overlay, popup and all.
                onWheel: function (e) { return e.stopPropagation(); }, children: _jsx(FamilyAccordionSelect, { familyHeader: t("sidebar.edit_pump.select_pump_family"), itemHeader: t("sidebar.edit_pump.select_pump_model"), noItemImage: noPumpImage, familyOptions: pumpFamilyOptions, familyImages: pumpFamilyImages, selectedFamilies: selectedPumpFamilies, onSelectFamily: onPickPumpFamily, itemOptions: pumpItemOptions, selectedItemValues: selectedValues, selectedItemImage: selectedImage, onSelectItem: onSelectPump, itemContent: _jsx(PumpChoiceSelect, { pumpChoice: pumpChoice, feedPressureUnit: feedPressureUnit }), scrollIntoPosition: "nearest" }) }) }) }));
};
